/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useQuery } from '@tanstack/react-query';
import { ClientsService } from '@services/requests/housien-services/ClientsService';

/**
 * The attendance's client record, whole.
 *
 * Two panels on the screen need it — the contact channels and what the
 * client is looking for — and both read it from this one cache entry
 * rather than listing the client twice.
 *
 * @param agencyId - The owning agency.
 * @param clientId - The attendance's client; absent disables the query.
 * @returns The record, or undefined while it loads or when there is no client.
 */
export function useDealClientRecord(
  agencyId: string,
  clientId?: string,
): Record<string, any> | undefined {
  const { data } = useQuery({
    queryKey: ['clients', agencyId, 'record', clientId] as const,
    queryFn: () => ClientsService.getAgencyClient(agencyId, clientId as string),
    enabled: Boolean(agencyId && clientId),
  });
  return data as Record<string, any> | undefined;
}
